import React from "react";
import { XAxisTickContentProps } from "recharts";

type WrappedLabelProps = {
  maxWidth?: number;
  fontSize?: string;
  lineHeight?: number;
} & XAxisTickContentProps;

const canvas = document.createElement("canvas");

export const WrappedLabel: React.FC<WrappedLabelProps> = ({
  x,
  y,
  payload,
  fontSize = "12px",
  fill,
  lineHeight = 14,
  textAnchor,
  ...props
}) => {
  const padding: number =
    typeof props.padding === "object"
      ? props.padding.left ?? 0 + (props.padding.right ?? 0)
      : 5;
  const maxWidth =
    props.maxWidth ??
    (typeof props.width === "number"
      ? props.width / props.visibleTicksCount - padding * 2
      : 50);
  // Sync the canvas font with the actual rendered font so measureText is accurate
  const canvasContext = canvas.getContext("2d");
  if (canvasContext) {
    canvasContext.font = `${fontSize} sans-serif`;
  }

  const lines = wrapText(payload.value, maxWidth, canvas);

  return (
    <text x={x} y={y} textAnchor={textAnchor} fontSize={fontSize} fill={fill}>
      {lines.map((line, i) => (
        <tspan x={x} dy={i === 0 ? 12 : lineHeight} key={i}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

function wrapText(
  label: string,
  maxWidth: number,
  canvas: HTMLCanvasElement
): string[] {
  const ctx = canvas.getContext("2d");
  if (!ctx) return [label];

  const measure = (text: string) => ctx.measureText(text).width;

  let currentLine = "";
  const lines: string[] = [];

  for (const word of label.split(" ")) {
    if (measure(word) > maxWidth) {
      // Word itself is too wide — hyphenate it
      if (currentLine) lines.push(currentLine);
      let fragment = "";
      for (const char of word) {
        if (measure(fragment + char) >= maxWidth) {
          lines.push(fragment + "-");
          fragment = char;
        } else {
          fragment += char;
        }
      }
      currentLine = fragment;
    } else if (
      measure(currentLine ? `${currentLine} ${word}` : word) >= maxWidth
    ) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = currentLine ? `${currentLine} ${word}` : word;
    }
  }

  if (currentLine) lines.push(currentLine);
  return lines;
}
